import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GraduationCap, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { trackFunnelEvent } from '../lib/funnel-tracker';

export default function Register() {
    const [nombre, setNombre] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    // Track que el usuario INICIÓ el registro (llegó a la pantalla)
    useEffect(() => {
        trackFunnelEvent('register_started');
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!nombre || !email || !password || !confirmPassword) {
            toast.error('Por favor completa todos los campos');
            trackFunnelEvent('abandoned', { last_action: 'register_missing_fields' });
            return;
        }
        if (password !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            trackFunnelEvent('abandoned', { last_action: 'register_password_mismatch' });
            return;
        }
        if (password.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            trackFunnelEvent('abandoned', { last_action: 'register_password_too_short' });
            return;
        }
        setLoading(true);
        try {
            const result = await register(nombre, email, password);
            trackFunnelEvent('register_completed', {
                user_id: result?.user?.id, meta: { provider: 'email' },
            });
            trackFunnelEvent('first_session', { user_id: result?.user?.id });
            toast.success('¡Cuenta creada exitosamente!');
            // Fix 1+2: en vez de /perfil, vamos directo al onboarding express
            navigate('/onboarding');
        } catch (error) {
            console.error('Register error:', error);
            const errMsg = error.response?.data?.detail || 'Error al crear la cuenta';
            toast.error(errMsg);
            trackFunnelEvent('abandoned', { last_action: 'register_api_error', error: errMsg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="mb-8 text-center">
                    <Link to="/" className="inline-flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900">
                            <GraduationCap className="h-7 w-7 text-white" />
                        </div>
                        <div className="text-left leading-tight">
                            <span className="block font-heading text-lg font-bold text-slate-900">Portafolio Digital Docente</span>
                            <p className="text-xs text-slate-500 tracking-wide">Acontista Educa</p>
                        </div>
                    </Link>
                </div>

                <Card className="border-slate-200 shadow-lg">
                    <CardHeader className="space-y-1 pb-6">
                        <CardTitle className="font-heading text-2xl">Crear Cuenta</CardTitle>
                        <CardDescription>
                            Completa tus datos para comenzar
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <GoogleLoginButton testId="register-google-btn" />
                        <div className="my-4 flex items-center gap-3">
                            <span className="flex-1 h-px bg-slate-200" />
                            <span className="text-[10px] uppercase tracking-widest text-slate-400">o regístrate con email</span>
                            <span className="flex-1 h-px bg-slate-200" />
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="nombre">Nombre Completo</Label>
                                <Input
                                    id="nombre"
                                    type="text"
                                    placeholder="María González"
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    disabled={loading}
                                    data-testid="register-nombre"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Correo Electrónico</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="tu@correo.cl"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    data-testid="register-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Contraseña</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Mínimo 6 caracteres"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        data-testid="register-password"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Repite tu contraseña"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={loading}
                                    data-testid="register-confirm-password"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                                data-testid="register-submit"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creando cuenta...
                                    </>
                                ) : (
                                    'Crear Cuenta'
                                )}
                            </Button>
                        </form>
                        <div className="mt-6 text-center text-sm">
                            <span className="text-slate-500">¿Ya tienes cuenta? </span>
                            <Link to="/login" className="font-medium text-slate-900 hover:underline">
                                Inicia sesión
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
